"use client";
import React, { FC } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { LatestArticlesCarouselProps } from "~/bff/components/LatestArticlesCarousel";
import { getLinkUrl } from "~/helpers/locales";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { EvergageContentZones } from "~/lib/salesforce/evergage/types";

import { ProductCarousel } from "../carousel/components/product-carousel/component";
import { CTALinkButton } from "../cta-link-button/component";

import { LATEST_ARTICLES_CAROUSEL_CLASSES } from "./constants";
import { LatestArticlesCarouselContainer } from "./styled";

export type LatestArticlesPropsExtended = {
  layout?: "left" | "right";
  className?: string;
} & LatestArticlesCarouselProps;

export const LatestArticlesCarousel: FC<LatestArticlesPropsExtended> = ({
  meta,
  className,
  link,
  title,
  subtitle,
  children,
  layout = "right",
  testAutomationId = "latest-articles-carousel",
}) => {
  const { locale } = useParams<GlobalPathParams>();
  const titleId = `latest-articles-carousel-title${meta?.deliveryId ? `-${meta?.deliveryId}` : ""}`;

  const idValue = `latest-articles-carousel${meta?.deliveryId ? `-${meta?.deliveryId}` : ""}`;

  return (
    <LatestArticlesCarouselContainer
      className={className}
      role="group"
      aria-labelledby={titleId}
      id={idValue}
      {...getTestAutomationProps(testAutomationId)}
      data-evergage-zone={EvergageContentZones.ARTICLES_CAROUSEL}
    >
      <ProductCarousel
        layout={layout}
        header={
          <Box
            className={LATEST_ARTICLES_CAROUSEL_CLASSES.header}
            {...getTestAutomationProps("header")}
          >
            <Typography
              className={LATEST_ARTICLES_CAROUSEL_CLASSES.subtitle}
              {...getTestAutomationProps("subtitle")}
            >
              {subtitle}
            </Typography>
            <Typography
              component="h2"
              id={titleId}
              className={LATEST_ARTICLES_CAROUSEL_CLASSES.title}
              {...getTestAutomationProps("title")}
            >
              {title}
            </Typography>
            {link && (
              <CTALinkButton
                href={getLinkUrl(locale, link?.url ?? "")}
                className={LATEST_ARTICLES_CAROUSEL_CLASSES.ctaLink}
              >
                {link.label}
              </CTALinkButton>
            )}
          </Box>
        }
      >
        {children as React.ReactNode}
      </ProductCarousel>
    </LatestArticlesCarouselContainer>
  );
};
